import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import Layout from '../components/Layout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import { Pagination } from '../components/ui/pagination';
import { Loader } from '../components/ui/loader';
import {
    ShoppingBag,
    RefreshCw,
    CheckCircle2,
    XCircle,
    AlertCircle,
    ArrowRight,
    Search,
    Filter,
    Plus,
    Upload,
    ExternalLink
} from 'lucide-react';
import axiosInstance from '../utils/axiosInstance';

const ITEMS_PER_PAGE = 50;

const Brands = () => {
    const [searchParams, setSearchParams] = useSearchParams();

    // View mode: 'mappings' or 'master-brands'
    const [viewMode, setViewMode] = useState(searchParams.get('view') || 'mappings');

    const [loading, setLoading] = useState(true);
    const [page, setPage] = useState(() => parseInt(searchParams.get('page') || '1', 10));
    const [totalPages, setTotalPages] = useState(1);
    const [totalItems, setTotalItems] = useState(0);
    const [webshopBrands, setWebshopBrands] = useState([]);
    const [brandMappings, setBrandMappings] = useState([]);
    const [stats, setStats] = useState({});

    // Master brands view data
    const [masterBrands, setMasterBrands] = useState([]);
    const [masterBrandsLoading, setMasterBrandsLoading] = useState(false);
    const [showManualOnly, setShowManualOnly] = useState(false);

    // Filters
    const [selectedFilter, setSelectedFilter] = useState(searchParams.get('status') || 'all');
    const [searchTerm, setSearchTerm] = useState(searchParams.get('search') || '');
    const [webshopBrandFilter, setWebshopBrandFilter] = useState(searchParams.get('webshop_brand') || '');

    const [syncing, setSyncing] = useState(false);
    const [autoMapping, setAutoMapping] = useState(false);
    const isInitialMount = useRef(true);

    useEffect(() => {
        document.title = 'Brand Management - Mappy';
        // Always fetch stats and webshop brands on mount (needed for both views)
        fetchStats();
        fetchWebshopBrands();
    }, []);

    // Fetch master brands when view mode changes
    useEffect(() => {
        if (viewMode === 'master-brands') {
            console.log('🔄 View mode is master-brands, fetching...');
            fetchMasterBrands();
        } else {
            // Reset master brands when switching away
            setMasterBrands([]);
            setMasterBrandsLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [viewMode, showManualOnly]);
    
    // Ensure master brands are fetched on initial load if viewMode is master-brands
    useEffect(() => {
        const urlView = searchParams.get('view') || 'mappings';
        if (urlView === 'master-brands' && masterBrands.length === 0 && !masterBrandsLoading) {
            console.log('🔄 Initial load detected for master-brands, fetching...');
            fetchMasterBrands();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Sync view mode from URL
    useEffect(() => {
        const urlView = searchParams.get('view') || 'mappings';
        if (urlView !== viewMode && (urlView === 'mappings' || urlView === 'master-brands')) {
            setViewMode(urlView);
        }
    }, [searchParams]);

    // Sync state from URL params and fetch data (only for mappings view)
    useEffect(() => {
        if (viewMode !== 'mappings') return;
        
        const urlPage = parseInt(searchParams.get('page') || '1', 10);
        const urlStatus = searchParams.get('status') || 'all';
        const urlSearch = searchParams.get('search') || '';
        const urlWebshopBrand = searchParams.get('webshop_brand') || '';
        
        console.log('🔄 URL sync effect triggered:', {
            urlPage,
            urlStatus,
            urlSearch,
            urlWebshopBrand,
            currentStatePage: page,
            currentStateFilter: selectedFilter
        });
        
        // Always update state to match URL (handles initial mount and URL changes)
        setPage(urlPage);
        setSelectedFilter(urlStatus);
        setSearchTerm(urlSearch);
        setWebshopBrandFilter(urlWebshopBrand);
        
        // Always fetch with URL params directly to ensure we get the right data
        fetchMappings(urlPage, urlStatus, urlSearch, urlWebshopBrand);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchParams, viewMode]);

    // Update URL when state changes (user interactions like clicking pagination)
    useEffect(() => {
        // Skip on initial mount - URL sync effect handles initial fetch
        if (isInitialMount.current) {
            isInitialMount.current = false;
            console.log('⏭️ Skipping state change effect on initial mount');
            return;
        }

        console.log('🔄 State change effect triggered:', {
            page,
            selectedFilter,
            searchTerm,
            webshopBrandFilter
        });

        // Build params from current state
        const currentParams = {};
        if (page > 1) currentParams.page = page.toString();
        if (selectedFilter !== 'all') currentParams.status = selectedFilter;
        if (searchTerm) currentParams.search = searchTerm;
        if (webshopBrandFilter) currentParams.webshop_brand = webshopBrandFilter;
        
        // Check if URL needs updating
        const currentUrlPage = searchParams.get('page');
        const currentUrlStatus = searchParams.get('status');
        const currentUrlSearch = searchParams.get('search');
        const currentUrlWebshopBrand = searchParams.get('webshop_brand');
        
        const urlNeedsUpdate = 
            (page > 1 ? page.toString() : null) !== currentUrlPage ||
            (selectedFilter !== 'all' ? selectedFilter : null) !== currentUrlStatus ||
            (searchTerm || null) !== currentUrlSearch ||
            (webshopBrandFilter || null) !== currentUrlWebshopBrand;
        
        console.log('🔗 URL update check:', {
            urlNeedsUpdate,
            currentParams,
            currentUrl: {
                page: currentUrlPage,
                status: currentUrlStatus,
                search: currentUrlSearch,
                webshop_brand: currentUrlWebshopBrand
            }
        });
        
        if (urlNeedsUpdate) {
            console.log('📝 Updating URL params:', currentParams);
            setSearchParams(currentParams, { replace: true });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page, selectedFilter, searchTerm, webshopBrandFilter]);

    const fetchWebshopBrands = async () => {
        try {
            const response = await axiosInstance.get('/api/tracking/webshop-brands/all/');
            setWebshopBrands(response.data);
        } catch (error) {
            console.error('Error fetching webshop brands:', error);
        }
    };

    const fetchStats = async () => {
        try {
            const response = await axiosInstance.get('/api/tracking/brand-mappings/stats/');
            setStats(response.data);
        } catch (error) {
            console.error('Error fetching stats:', error);
        }
    };

    const fetchMasterBrands = async () => {
        try {
            console.log('🔄 Fetching master brands...', { showManualOnly });
            setMasterBrandsLoading(true);
            setMasterBrands([]); // Clear previous data
            
            const params = new URLSearchParams();
            if (showManualOnly) {
                params.append('manual_only', 'true');
            }
            const url = `/api/tracking/webshop-brands/with_competitors/?${params.toString()}`;
            console.log('📡 API URL:', url);
            
            const startTime = Date.now();
            const response = await axiosInstance.get(url, {
                timeout: 30000 // 30 second timeout
            });
            const duration = Date.now() - startTime;
            
            console.log(`✅ Master brands response (${duration}ms):`, {
                count: response.data?.length || 0,
                firstBrand: response.data?.[0],
                data: response.data
            });
            
            if (Array.isArray(response.data)) {
                setMasterBrands(response.data);
            } else {
                console.error('❌ Invalid response format:', response.data);
                setMasterBrands([]);
            }
        } catch (error) {
            console.error('❌ Error fetching master brands:', error);
            console.error('Error response:', error.response);
            console.error('Error details:', error.response?.data || error.message);
            setMasterBrands([]); // Set empty array on error
            
            const errorMessage = error.response?.data?.error || error.message || 'Unknown error';
            alert(`Failed to load master brands: ${errorMessage}`);
        } finally {
            console.log('🏁 Setting loading to false');
            setMasterBrandsLoading(false);
        }
    };

    const fetchMappings = async (pageOverride = null, filterOverride = null, searchOverride = null, webshopBrandOverride = null) => {
        try {
            setLoading(true);
            // Use override values if provided, otherwise use current state
            const currentPage = pageOverride !== null ? pageOverride : page;
            const currentFilter = filterOverride !== null ? filterOverride : selectedFilter;
            const currentSearch = searchOverride !== null ? searchOverride : searchTerm;
            const currentWebshopBrand = webshopBrandOverride !== null ? webshopBrandOverride : webshopBrandFilter;
            
            const offset = (currentPage - 1) * ITEMS_PER_PAGE;
            
            console.log('🔍 fetchMappings called:', {
                currentPage,
                currentFilter,
                currentSearch,
                currentWebshopBrand,
                offset,
                limit: ITEMS_PER_PAGE,
                statePage: page,
                stateFilter: selectedFilter
            });
            
            const params = new URLSearchParams();
            params.append('limit', ITEMS_PER_PAGE);
            params.append('offset', offset);

            if (currentFilter !== 'all') {
                params.append('status', currentFilter);
            }
            if (currentSearch) {
                params.append('search', currentSearch);
            }
            if (currentWebshopBrand) {
                params.append('webshop_brand', currentWebshopBrand);
            }

            const url = `/api/tracking/brand-mappings/?${params.toString()}`;
            console.log('📡 Fetching from URL:', url);

            const response = await axiosInstance.get(url);

            console.log('✅ Response received:', {
                resultsCount: response.data.results?.length || 0,
                totalCount: response.data.count,
                firstResultId: response.data.results?.[0]?.id,
                firstResultName: response.data.results?.[0]?.competitor_brand_name,
                lastResultId: response.data.results?.[response.data.results.length - 1]?.id,
                lastResultName: response.data.results?.[response.data.results.length - 1]?.competitor_brand_name
            });

            setBrandMappings(response.data.results);
            setTotalItems(response.data.count);
            setTotalPages(Math.ceil(response.data.count / ITEMS_PER_PAGE));
            
            console.log('📊 State updated:', {
                brandMappingsLength: response.data.results?.length || 0,
                totalItems: response.data.count,
                totalPages: Math.ceil(response.data.count / ITEMS_PER_PAGE)
            });
        } catch (error) {
            console.error('❌ Error fetching brand mappings:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleSyncFromPIM = async () => {
        try {
            setSyncing(true);
            const response = await axiosInstance.post('/api/tracking/webshop-brands/sync_from_pim/');

            if (response.data.success) {
                alert(`Synced ${response.data.new_brands_created} new brands from PIM!`);
                fetchWebshopBrands();
                fetchStats();
            }
        } catch (error) {
            console.error('Error syncing brands:', error);
            alert('Failed to sync brands from PIM');
        } finally {
            setSyncing(false);
        }
    };

    const handleAutoMap = async () => {
        try {
            setAutoMapping(true);
            const response = await axiosInstance.post('/api/tracking/brand-mappings/auto_map/');

            if (response.data.success) {
                alert(`Auto-mapped ${response.data.auto_mapped} brands! ${response.data.open_for_review} remain open for review.`);
                fetchMappings();
                fetchStats();
            }
        } catch (error) {
            console.error('Error auto-mapping brands:', error);
            alert('Failed to auto-map brands');
        } finally {
            setAutoMapping(false);
        }
    };

    const handleUpdateMapping = async (mappingId, updates) => {
        try {
            await axiosInstance.patch(`/api/tracking/brand-mappings/${mappingId}/`, updates);
            fetchMappings();
            fetchStats();
        } catch (error) {
            console.error('Error updating mapping:', error);
            alert('Failed to update brand mapping');
        }
    };

    const handleStatusChange = async (mappingId, newStatus) => {
        await handleUpdateMapping(mappingId, { status: newStatus });
    };

    const handleCreateBrand = async (mapping) => {
        const newName = window.prompt(`Create new Master Brand for "${mapping.competitor_brand_name}"?`, mapping.competitor_brand_name);

        if (newName && newName.trim()) {
            try {
                const createResponse = await axiosInstance.post('/api/tracking/webshop-brands/', {
                    name: newName.trim()
                });

                const newBrandId = createResponse.data.id;
                await fetchWebshopBrands();
                await handleMapToBrand(mapping.id, newBrandId);

            } catch (error) {
                console.error('Error creating brand:', error);
                alert('Failed to create new brand. It might already exist.');
            }
        }
    };

    const handleMapToBrand = async (mappingId, webshopBrandId) => {
        await handleUpdateMapping(mappingId, {
            status: 'mapped',
            webshop_brand: webshopBrandId
        });
    };

    const getStatusBadge = (mapping) => {
        switch (mapping.status) {
            case 'mapped':
                if (mapping.webshop_brand_in_pim) {
                    return <Badge className="bg-green-500"><CheckCircle2 className="h-3 w-3 mr-1" />In Catalog</Badge>;
                } else {
                    return <Badge className="bg-orange-500"><CheckCircle2 className="h-3 w-3 mr-1" />Manual</Badge>;
                }
            case 'ignored':
                return <Badge variant="secondary"><XCircle className="h-3 w-3 mr-1" />Ignored</Badge>;
            case 'open':
                return <Badge variant="outline"><AlertCircle className="h-3 w-3 mr-1" />Open</Badge>;
            default:
                return <Badge>{mapping.status}</Badge>;
        }
    };

    // Show loading screen only for mappings view on initial load
    if (viewMode === 'mappings' && loading && page === 1 && brandMappings.length === 0) {
        return (
            <Layout>
                <div className="flex items-center justify-center min-h-screen">
                    <Loader />
                    <div className="text-muted-foreground ml-2">Loading brands...</div>
                </div>
            </Layout>
        );
    }
    
    // Show loading screen for master-brands view on initial load
    if (viewMode === 'master-brands' && masterBrandsLoading && masterBrands.length === 0) {
        return (
            <Layout>
                <div className="flex items-center justify-center min-h-screen">
                    <Loader />
                    <div className="text-muted-foreground ml-2">Loading master brands...</div>
                </div>
            </Layout>
        );
    }

    const renderMasterBrandsView = () => {
        return (
            <div className="space-y-6">
                {/* Filter */}
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                        <h2 className="text-2xl font-bold">Master Brands</h2>
                        <label className="flex items-center gap-2 cursor-pointer">
                            <input
                                type="checkbox"
                                checked={showManualOnly}
                                onChange={(e) => setShowManualOnly(e.target.checked)}
                                className="w-4 h-4 rounded border-gray-300"
                            />
                            <span className="text-sm text-muted-foreground">Show only manual/custom brands</span>
                        </label>
                    </div>
                    <div className="text-sm text-muted-foreground">
                        {masterBrands.length} {showManualOnly ? 'manual' : 'total'} brand{masterBrands.length !== 1 ? 's' : ''}
                    </div>
                </div>

                {/* Master Brands List - Table Layout */}
                {masterBrandsLoading ? (
                    <div className="flex items-center justify-center py-12">
                        <Loader />
                    </div>
                ) : masterBrands.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-12">
                        <ShoppingBag className="h-12 w-12 text-muted-foreground mb-4" />
                        <h3 className="text-lg font-semibold mb-2">No brands found</h3>
                        <p className="text-muted-foreground text-center">
                            {showManualOnly 
                                ? 'No manual/custom brands found.' 
                                : 'No master brands found.'}
                        </p>
                    </div>
                ) : (
                    <div className="border rounded-md bg-card">
                        {/* Header */}
                        <div className="grid grid-cols-12 gap-4 px-4 py-3 bg-muted/40 font-medium text-xs text-muted-foreground border-b uppercase tracking-wider">
                            <div className="col-span-4 lg:col-span-3">Brand Name</div>
                            <div className="col-span-2 lg:col-span-1">Status</div>
                            <div className="col-span-2 lg:col-span-1 text-center">Count</div>
                            <div className="col-span-4 lg:col-span-7">Found on Webshops</div>
                        </div>

                        {/* Rows */}
                        <div className="divide-y">
                            {masterBrands.map((brand) => (
                                <div key={brand.id} className="grid grid-cols-12 gap-4 px-4 py-3 items-center text-sm hover:bg-muted/5 transition-colors">
                                    {/* Brand Name */}
                                    <div className="col-span-4 lg:col-span-3">
                                        <span className="font-medium">{brand.name}</span>
                                    </div>

                                    {/* Status */}
                                    <div className="col-span-2 lg:col-span-1">
                                        {brand.in_pim ? (
                                            <Badge className="bg-blue-500 text-white">In Catalog</Badge>
                                        ) : (
                                            <Badge className="bg-orange-500 text-white">Manual</Badge>
                                        )}
                                    </div>

                                    {/* Count */}
                                    <div className="col-span-2 lg:col-span-1 text-center">
                                        <span className="font-semibold">{brand.competitor_count}</span>
                                    </div>

                                    {/* Competitors */}
                                    <div className="col-span-4 lg:col-span-7">
                                        {brand.competitors.length > 0 ? (
                                            <div className="flex flex-wrap gap-1.5 items-center">
                                                {brand.competitors.map((competitor) => (
                                                    <div
                                                        key={competitor.id}
                                                        className="group relative"
                                                        title={competitor.name}
                                                    >
                                                        {(competitor.brand_url || competitor.website) ? (
                                                            <a
                                                                href={competitor.brand_url || competitor.website}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                                className="inline-block"
                                                            >
                                                                {competitor.logo_url ? (
                                                                    <img
                                                                        src={competitor.logo_url}
                                                                        alt={competitor.name}
                                                                        className="h-6 w-6 object-contain rounded border border-border bg-background p-0.5 hover:border-primary transition-colors cursor-pointer"
                                                                        onError={(e) => {
                                                                            e.target.style.display = 'none';
                                                                            if (e.target.nextSibling) {
                                                                                e.target.nextSibling.style.display = 'flex';
                                                                            }
                                                                        }}
                                                                    />
                                                                ) : (
                                                                    <div className="h-6 w-6 rounded border border-border bg-muted flex items-center justify-center text-[10px] font-medium text-muted-foreground hover:border-primary hover:bg-muted/80 transition-colors cursor-pointer">
                                                                        {competitor.name.substring(0, 2).toUpperCase()}
                                                                    </div>
                                                                )}
                                                            </a>
                                                        ) : (
                                                            <>
                                                                {competitor.logo_url ? (
                                                                    <img
                                                                        src={competitor.logo_url}
                                                                        alt={competitor.name}
                                                                        className="h-6 w-6 object-contain rounded border border-border bg-background p-0.5 opacity-60 cursor-not-allowed"
                                                                        onError={(e) => {
                                                                            e.target.style.display = 'none';
                                                                            if (e.target.nextSibling) {
                                                                                e.target.nextSibling.style.display = 'flex';
                                                                            }
                                                                        }}
                                                                    />
                                                                ) : (
                                                                    <div className="h-6 w-6 rounded border border-border bg-muted flex items-center justify-center text-[10px] font-medium text-muted-foreground cursor-not-allowed opacity-60">
                                                                        {competitor.name.substring(0, 2).toUpperCase()}
                                                                    </div>
                                                                )}
                                                            </>
                                                        )}
                                                        {/* Tooltip on hover */}
                                                        <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 bg-gray-900 text-white text-xs rounded opacity-0 group-hover:opacity-100 pointer-events-none whitespace-nowrap z-10 transition-opacity">
                                                            {competitor.name}
                                                            {(competitor.brand_url || competitor.website) && (
                                                                <span className="block text-[10px] text-gray-300 mt-0.5">
                                                                    {(competitor.brand_url || competitor.website).replace(/^https?:\/\//, '')}
                                                                </span>
                                                            )}
                                                            <div className="absolute top-full left-1/2 transform -translate-x-1/2 -mt-1">
                                                                <div className="border-4 border-transparent border-t-gray-900"></div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        ) : (
                                            <span className="text-xs text-muted-foreground">No competitors mapped</span>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </div>
        );
    };

    return (
        <Layout>
            <div className="container mx-auto p-6 space-y-6">
                <div className="flex items-center justify-between">
                    <div>
                        <h1 className="text-3xl font-bold tracking-tight">Brand Management</h1>
                        <p className="text-muted-foreground mt-1">
                            Map competitor brands to your webshop catalog
                        </p>
                    </div>
                    <div className="flex gap-2">
                        {viewMode === 'mappings' && (
                            <>
                                <Button
                                    onClick={handleSyncFromPIM}
                                    disabled={syncing}
                                    variant="outline"
                                >
                                    <RefreshCw className={`mr-2 h-4 w-4 ${syncing ? 'animate-spin' : ''}`} />
                                    {syncing ? 'Syncing...' : 'Sync from PIM'}
                                </Button>
                                <Button
                                    onClick={handleAutoMap}
                                    disabled={autoMapping || stats.total === 0}
                                >
                                    <CheckCircle2 className="mr-2 h-4 w-4" />
                                    {autoMapping ? 'Auto-Mapping...' : 'Auto-Map'}
                                </Button>
                            </>
                        )}
                    </div>
                </div>

                {/* Tabs */}
                <div className="flex gap-2 border-b">
                    <button
                        onClick={() => {
                            setViewMode('mappings');
                            setSearchParams({ ...Object.fromEntries(searchParams), view: 'mappings' }, { replace: true });
                        }}
                        className={`px-4 py-2 font-medium text-sm border-b-2 transition-colors ${
                            viewMode === 'mappings'
                                ? 'border-primary text-primary'
                                : 'border-transparent text-muted-foreground hover:text-foreground'
                        }`}
                    >
                        Brand Mappings
                    </button>
                    <button
                        onClick={() => {
                            setViewMode('master-brands');
                            setSearchParams({ ...Object.fromEntries(searchParams), view: 'master-brands' }, { replace: true });
                        }}
                        className={`px-4 py-2 font-medium text-sm border-b-2 transition-colors ${
                            viewMode === 'master-brands'
                                ? 'border-primary text-primary'
                                : 'border-transparent text-muted-foreground hover:text-foreground'
                        }`}
                    >
                        Master Brands
                    </button>
                </div>

                <Separator />

                {viewMode === 'master-brands' ? renderMasterBrandsView() : (
                    <>
                        {/* Compact Stats Row */}
                <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-2">
                    {/* Master Stats */}
                    <div className="flex flex-col p-3 bg-muted/20 rounded-lg border">
                        <span className="text-xs text-muted-foreground">Total Master</span>
                        <span className="text-lg font-bold">{stats.master_brands_total || 0}</span>
                    </div>
                    <div className="flex flex-col p-3 bg-blue-50/50 rounded-lg border border-blue-100">
                        <span className="text-xs text-blue-600">In Catalog (PIM)</span>
                        <span className="text-lg font-bold text-blue-700">{stats.master_brands_in_pim || 0}</span>
                    </div>
                    <div className="flex flex-col p-3 bg-indigo-50/50 rounded-lg border border-indigo-100">
                        <span className="text-xs text-indigo-600">Manual / Custom</span>
                        <span className="text-lg font-bold text-indigo-700">{stats.master_brands_manual || 0}</span>
                    </div>

                    {/* Spacer/Separator equivalent */}
                    <div className="hidden lg:block w-px bg-border mx-auto h-full"></div>

                    {/* Mapping Stats */}
                    <div
                        className={`flex flex-col p-3 rounded-lg border cursor-pointer hover:bg-muted/50 transition-colors ${selectedFilter === 'all' ? 'ring-2 ring-primary bg-muted/50' : 'bg-muted/10'}`}
                        onClick={() => setSelectedFilter('all')}
                    >
                        <span className="text-xs text-muted-foreground">Detected Brands</span>
                        <span className="text-lg font-bold">{stats.total || 0}</span>
                    </div>
                    <div
                        className={`flex flex-col p-3 rounded-lg border cursor-pointer hover:bg-green-50 transition-colors ${selectedFilter === 'mapped' ? 'ring-2 ring-green-600 bg-green-50' : 'bg-green-50/30'}`}
                        onClick={() => setSelectedFilter('mapped')}
                    >
                        <span className="text-xs text-green-600">In Catalog</span>
                        <span className="text-lg font-bold text-green-700">{stats.mapped || 0}</span>
                    </div>
                    <div
                        className={`flex flex-col p-3 rounded-lg border cursor-pointer hover:bg-orange-50 transition-colors ${selectedFilter === 'open' ? 'ring-2 ring-orange-400 bg-orange-50' : 'bg-orange-50/30'}`}
                        onClick={() => setSelectedFilter('open')}
                    >
                        <span className="text-xs text-orange-600">Open</span>
                        <span className="text-lg font-bold text-orange-700">{stats.open || 0}</span>
                    </div>
                    <div
                        className={`flex flex-col p-3 rounded-lg border cursor-pointer hover:bg-indigo-50 transition-colors ${selectedFilter === 'manual' ? 'ring-2 ring-indigo-400 bg-indigo-50' : 'bg-indigo-50/30'}`}
                        onClick={() => setSelectedFilter('manual')}
                    >
                        <span className="text-xs text-indigo-600">Manual</span>
                        <span className="text-lg font-bold text-indigo-700">{stats.manual || 0}</span>
                    </div>
                </div>

                {/* Search and Filter */}
                <div className="flex flex-col md:flex-row gap-4">
                    <div className="relative flex-1">
                        <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                        <input
                            type="text"
                            placeholder="Search brands..."
                            className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 pl-10"
                            value={searchTerm}
                            onChange={(e) => {
                                setSearchTerm(e.target.value);
                                setPage(1);
                            }}
                        />
                    </div>

                    <div className="w-full md:w-64">
                        <select
                            className="w-full h-10 border rounded-md px-3 text-sm bg-background"
                            value={webshopBrandFilter}
                            onChange={(e) => {
                                setWebshopBrandFilter(e.target.value);
                                setPage(1);
                            }}
                        >
                            <option value="">Filter by Master Brand...</option>
                            {webshopBrands.map((brand) => (
                                <option key={brand.id} value={brand.id}>
                                    {brand.name}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div className="flex gap-2">
                        {['all', 'open', 'mapped', 'manual', 'ignored'].map(status => (
                            <Button
                                key={status}
                                variant={selectedFilter === status ? 'default' : 'outline'}
                                onClick={() => {
                                    setSelectedFilter(status);
                                    setPage(1);
                                }}
                                size="sm"
                                className="capitalize"
                            >
                                {status === 'mapped' ? 'In Catalog' : status}
                            </Button>
                        ))}
                    </div>
                </div>

                {/* Brand Mappings List (Table Layout) */}
                <div className="border rounded-md bg-card">
                    {/* Header */}
                    <div className="grid grid-cols-12 gap-4 px-4 py-3 bg-muted/40 font-medium text-xs text-muted-foreground border-b uppercase tracking-wider">
                        <div className="col-span-4 lg:col-span-3">Competitor Brand</div>
                        <div className="col-span-2 lg:col-span-2">Source</div>
                        <div className="col-span-2 lg:col-span-1">Status</div>
                        <div className="col-span-4 lg:col-span-4">Master Brand (Map To)</div>
                        <div className="col-span-2 lg:col-span-2 text-right">Actions</div>
                    </div>

                    {/* Rows */}
                    <div className="divide-y">
                        {brandMappings.length === 0 ? (
                            <div className="flex flex-col items-center justify-center py-12">
                                <ShoppingBag className="h-12 w-12 text-muted-foreground mb-4" />
                                <h3 className="text-lg font-semibold mb-2">No brands found</h3>
                                <p className="text-muted-foreground text-center mb-4 max-w-sm">
                                    {brandMappings.length === 0 && !searchTerm && selectedFilter === 'all'
                                        ? 'Start by syncing brands from PIM and auto-mapping them.'
                                        : 'No brands match your current filter.'}
                                </p>
                            </div>
                        ) : (
                            brandMappings.map((mapping) => (
                                <div key={mapping.id} className="grid grid-cols-12 gap-4 px-4 py-2 items-center text-sm hover:bg-muted/5 transition-colors group">
                                    {/* Competitor Brand */}
                                    <div className="col-span-4 lg:col-span-3 truncate flex items-center gap-2" title={mapping.competitor_brand_name}>
                                        {mapping.competitor_brand_url ? (
                                            <a
                                                href={mapping.competitor_brand_url}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="font-medium hover:underline flex items-center gap-1 group/link"
                                            >
                                                {mapping.competitor_brand_name}
                                                <ExternalLink className="h-3 w-3 opacity-0 group-hover/link:opacity-100 transition-opacity" />
                                            </a>
                                        ) : (
                                            <span className="font-medium">{mapping.competitor_brand_name}</span>
                                        )}
                                    </div>

                                    {/* Source */}
                                    <div className="col-span-2 lg:col-span-2 text-muted-foreground truncate" title={mapping.competitor_name}>
                                        {mapping.competitor_name}
                                    </div>

                                    {/* Status */}
                                    <div className="col-span-2 lg:col-span-1">
                                        {getStatusBadge(mapping)}
                                    </div>

                                    {/* Master Brand / Selection */}
                                    <div className="col-span-4 lg:col-span-4">
                                        {mapping.status === 'mapped' && mapping.webshop_brand_name ? (
                                            <div className="flex items-center gap-2">
                                                <button
                                                    onClick={() => {
                                                        setWebshopBrandFilter(mapping.webshop_brand.toString());
                                                        setPage(1);
                                                    }}
                                                    className={`font-medium truncate hover:underline text-left ${mapping.webshop_brand_in_pim ? 'text-blue-600' : 'text-orange-600'}`}
                                                    title="Filter by this Master Brand"
                                                >
                                                    {mapping.webshop_brand_name}
                                                </button>
                                                {mapping.webshop_brand_in_pim ? (
                                                    <Badge variant="secondary" className="text-[10px] h-5 px-1 bg-blue-50 text-blue-700 border-blue-200 whitespace-nowrap">
                                                        In Catalog
                                                    </Badge>
                                                ) : (
                                                    <Badge variant="secondary" className="text-[10px] h-5 px-1 bg-orange-50 text-orange-700 border-orange-200 whitespace-nowrap">
                                                        Manual
                                                    </Badge>
                                                )}
                                            </div>
                                        ) : mapping.status !== 'mapped' ? (
                                            <div className="flex items-center gap-2">
                                                <select
                                                    className="h-8 w-full max-w-[200px] border rounded-md px-2 text-sm bg-background"
                                                    defaultValue=""
                                                    onChange={(e) => {
                                                        if (e.target.value) {
                                                            handleMapToBrand(mapping.id, parseInt(e.target.value));
                                                        }
                                                    }}
                                                >
                                                    <option value="" disabled>Map to...</option>
                                                    {webshopBrands.map((brand) => (
                                                        <option key={brand.id} value={brand.id}>
                                                            {brand.name}
                                                        </option>
                                                    ))}
                                                </select>
                                                <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    className="h-8 w-8 p-0"
                                                    onClick={() => handleCreateBrand(mapping)}
                                                    title="Create new Master Brand"
                                                >
                                                    <Plus className="h-4 w-4" />
                                                </Button>
                                            </div>
                                        ) : null}
                                    </div>

                                    {/* Actions */}
                                    <div className="col-span-2 lg:col-span-2 flex justify-end gap-2 opacity-50 group-hover:opacity-100 transition-opacity">
                                        {mapping.status === 'open' && (
                                            <Button
                                                variant="ghost"
                                                size="sm"
                                                className="h-8 text-muted-foreground hover:text-destructive"
                                                onClick={() => handleStatusChange(mapping.id, 'ignored')}
                                                title="Ignore"
                                            >
                                                <XCircle className="h-4 w-4" />
                                            </Button>
                                        )}

                                        {(mapping.status === 'mapped' || mapping.status === 'ignored') && (
                                            <Button
                                                variant="ghost"
                                                size="sm"
                                                className="h-8 text-muted-foreground"
                                                onClick={() => handleStatusChange(mapping.id, 'open')}
                                                title={mapping.status === 'mapped' ? 'Unmap' : 'Reopen'}
                                            >
                                                {mapping.status === 'mapped' ? 'Unmap' : 'Reopen'}
                                            </Button>
                                        )}
                                    </div>
                                </div>
                            ))
                        )}
                    </div>
                </div>

                        <Pagination
                            currentPage={page}
                            totalPages={totalPages}
                            onPageChange={setPage}
                            pageSize={ITEMS_PER_PAGE}
                            totalItems={totalItems}
                        />
                    </>
                )}
            </div>
        </Layout>
    );
};

export default Brands;
